import { ChessTermType, TermAction, } from "@chesshotel/types";
import { Chess, PAWN, WHITE, BLACK, } from "@chesshotel/chess";
import { exchangeEvaluation, getActionTypes, getFen, uci2move, } from "../util.js";
import { isValidFenMove } from "../tactics/util.js";
import { detectDiscoveredAttack } from "./discoveredAttack.js";
import { detectFork } from "./fork.js";
import { detectPinMove, detectUnpin } from "./pin.js";
import { detectSkewer } from "./skewer.js";
import { detectRemoveDefender, detectRemoveDefenderThreat, } from "./removeDefender.js";
const tacticsCache = new Map();
const tacticThreatsCache = new Map();
const TACTICS_CACHE_CAP = 5000;
export function clearTacticsCaches() {
    tacticsCache.clear();
    tacticThreatsCache.clear();
}
function cloneChessTerm(term) {
    if (!term)
        return null;
    return {
        ...term,
        arrows: term.arrows ? [...term.arrows] : undefined,
        translationKeys: term.translationKeys
            ? { ...term.translationKeys }
            : undefined,
    };
}
export function detectTactics(fen, move) {
    const cacheKey = `${fen}|${move}`;
    if (tacticsCache.has(cacheKey)) {
        return cloneChessTerm(tacticsCache.get(cacheKey));
    }
    const allTacticMoves = [];
    const discoveredAttack = detectDiscoveredAttack(fen, move);
    if (discoveredAttack) {
        allTacticMoves.push(discoveredAttack);
    }
    const fork = detectFork(fen, move);
    if (fork) {
        allTacticMoves.push(fork);
    }
    const pin = detectPinMove(fen, move);
    if (pin) {
        allTacticMoves.push(pin);
    }
    const unpin = detectUnpin(fen, move);
    if (unpin) {
        allTacticMoves.push(unpin);
    }
    const skewer = detectSkewer(fen, move);
    if (skewer) {
        allTacticMoves.push(skewer);
    }
    const removeDefender = detectRemoveDefender(fen, move);
    if (removeDefender) {
        allTacticMoves.push(removeDefender);
    }
    const removeDefenderThreat = !removeDefender && detectRemoveDefenderThreat(fen, move);
    if (removeDefenderThreat) {
        allTacticMoves.push(removeDefenderThreat);
    }
    const sortedTacticMoves = allTacticMoves.sort((a, b) => b.maxGain - a.maxGain);
    if (sortedTacticMoves.length > 0) {
        const best = sortedTacticMoves[0];
        if (tacticsCache.size > TACTICS_CACHE_CAP) {
            tacticsCache.clear();
        }
        tacticsCache.set(cacheKey, cloneChessTerm(best));
        return cloneChessTerm(best);
    }
    if (tacticsCache.size > TACTICS_CACHE_CAP) {
        tacticsCache.clear();
    }
    tacticsCache.set(cacheKey, null);
    return null;
}
function getBlockedMoves(fen, moveStr) {
    const chess = new Chess(fen);
    const possibleMoves = chess.uciMoves({});
    chess.move(uci2move(moveStr));
    chess.changeTurn();
    const blockedMoves = chess.uciMoves({}).filter((move) => {
        return !possibleMoves.includes(move);
    });
    return blockedMoves;
}
export function detectTacticThreats(fen, moveStr) {
    const cacheKey = `${fen}|${moveStr}`;
    if (tacticThreatsCache.has(cacheKey)) {
        const cached = cloneChessTerm(tacticThreatsCache.get(cacheKey));
        if (!cached) {
            return null;
        }
        cached.arrows = cached.arrows || [];
        if (cached.move) {
            cached.arrows.push({
                from: cached.move.from,
                to: cached.move.to,
            });
        }
        return cached;
    }
    const chess = new Chess(fen);
    const color = chess.turn();
    const moveObj = uci2move(moveStr);
    const piece = chess.get(moveObj.from);
    const possibleMoves = chess
        .moves({
        square: moveObj.from,
        piece: piece.type,
        verbose: true,
    })
        .map((move) => move.to);
    const moveResult = chess.move(uci2move(moveStr));
    if (!moveResult) {
        return null;
    }
    if (chess.inCheck()) {
        return null;
    }
    const evaluation = exchangeEvaluation(chess.fen(), moveObj.to, {
        backtrackBadMoves: true,
    });
    if (evaluation.gain > 0) {
        return null;
    }
    chess.changeTurn();
    const allTacticMoves = [];
    const blockedMoves = getBlockedMoves(fen, moveStr);
    const nextMovesByPiece = chess.uciMoves({
        square: moveResult.to,
        piece: moveResult.piece,
        verbose: true,
    });
    for (const move of blockedMoves) {
        const discoveredAttack = detectDiscoveredAttack(chess.fen(), move);
        if (discoveredAttack) {
            allTacticMoves.push(discoveredAttack);
        }
    }
    for (const move of nextMovesByPiece) {
        const moveTo = move.slice(2, 4);
        if (possibleMoves.includes(moveTo)) {
            continue;
        }
        const fork = detectFork(chess.fen(), move);
        if (fork) {
            allTacticMoves.push(fork);
        }
        const pin = detectPinMove(chess.fen(), move);
        if (pin) {
            if (pin.attackedPiece.type != PAWN) {
                allTacticMoves.push(pin);
            }
        }
        const skewer = detectSkewer(chess.fen(), move);
        if (skewer) {
            allTacticMoves.push(skewer);
        }
    }
    const sortedTacticMoves = allTacticMoves.sort((a, b) => b.maxGain - a.maxGain);
    if (sortedTacticMoves.length > 0) {
        const base = sortedTacticMoves[0];
        const cached = cloneChessTerm(base);
        if (tacticThreatsCache.size > TACTICS_CACHE_CAP) {
            tacticThreatsCache.clear();
        }
        tacticThreatsCache.set(cacheKey, cached);
        const chessTerm = cloneChessTerm(cached);
        chessTerm.arrows = chessTerm.arrows || [];
        if (chessTerm.move) {
            chessTerm.arrows.push({
                from: chessTerm.move.from,
                to: chessTerm.move.to,
            });
        }
        return {
            ...chessTerm,
            threatMove: moveResult,
        };
    }
    if (tacticThreatsCache.size > TACTICS_CACHE_CAP) {
        tacticThreatsCache.clear();
    }
    tacticThreatsCache.set(cacheKey, null);
    return null;
}
export function detectNegativeTactics(position, lastPosition) {
    const opponentMove = position.pvMoves[0];
    const user = detectTactics(position.fen, opponentMove);
    if (user?.term == ChessTermType.UNPIN)
        return null;
    if (user) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            user.arrows = user.arrows || [];
            user.arrows.push({ from: user.move.from, to: user.move.to });
            if (user.term == ChessTermType.PIN) {
                user.translationKeys.color =
                    position.moveIndex % 2 == 0 ? WHITE : BLACK;
            }
            return {
                ...user,
                termAction: TermAction.ALLOW,
                pvMoves: position.pvMoves?.slice(0, 4),
            };
        }
        const best = detectTactics(bestFen, opponentMove);
        if (!best) {
            user.arrows = user.arrows || [];
            user.arrows.push({ from: user.move.from, to: user.move.to });
            if (user.term == ChessTermType.PIN) {
                user.translationKeys.color =
                    position.moveIndex % 2 == 0 ? WHITE : BLACK;
            }
            return {
                ...user,
                termAction: TermAction.ALLOW,
                pvMoves: position.pvMoves?.slice(0, 4),
            };
        }
    }
    const user2 = detectTactics(lastPosition.fen, position.move);
    const best2 = detectTactics(lastPosition.fen, lastPosition.bestMove);
    if ((best2 && !user2) || (best2 && best2.term != user2?.term)) {
        best2.arrows = best2.arrows || [];
        best2.arrows.push({ from: best2.move.from, to: best2.move.to });
        return {
            ...best2,
            termAction: TermAction.MISS,
            pvMoves: lastPosition.pvMoves?.slice(0, 4),
        };
    }
}
export function detectPositiveTactics(position, lastPosition) {
    const user = detectTactics(lastPosition.fen, position.move);
    if (user) {
        let pvFollowsTactic = false;
        let pvMoves = null;
        if (user.followMoves) {
            pvFollowsTactic = position.pvMoves.some((followMove) => user.followMoves.includes(followMove));
            if (pvFollowsTactic) {
                const { actions: actionTypes } = getActionTypes(position.fen, position.pvMoves);
                let lastActionIndex = actionTypes.findIndex((action, index) => action != "capture" && index >= 4);
                pvMoves =
                    lastActionIndex != -1
                        ? position.pvMoves?.slice(0, lastActionIndex)
                        : position.pvMoves?.slice(0, 4);
            }
        }
        return {
            ...user,
            termAction: user.termAction || TermAction.PERFORM,
            pvMoves: pvFollowsTactic ? pvMoves : null,
        };
    }
}
export function detectPositiveTacticThreats(position, lastPosition) {
    const tacticThreat = detectTacticThreats(lastPosition.fen, position.move);
    if (tacticThreat) {
        tacticThreat.arrows = tacticThreat.arrows
            ? [...tacticThreat.arrows, tacticThreat.move]
            : [tacticThreat.move];
        return {
            ...tacticThreat,
            maxGain: tacticThreat.maxGain * 0.75,
            termAction: TermAction.PERFORM_THREAT,
            pvMoves: position.pvMoves?.slice(0, 4),
        };
    }
}
//# sourceMappingURL=index.js.map